import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, ConfirmDialog, Field, Textarea } from "@etain/ui";
import { Clock3, PhoneCall, PhoneOff, Save, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import {
  api,
  ApiError,
  asRecord,
  entityId,
  readBoolean,
  readNumber,
  readString,
  type JsonRecord,
} from "../lib/api.js";
import { useNotifications } from "./Notifications.js";
import { crmOrganizationPath } from "../lib/crm-navigation.js";

function statusLabel(status: string, direction: string, providerEventId: string) {
  const labels: Record<string, string> = {
    initiating: "Anruf wird vorbereitet",
    ringing: "Eingehender Anruf",
    dialing:
      direction === "inbound"
        ? "Eingehender Anruf"
        : providerEventId
          ? "Anruf läuft"
          : "Über Placetel gestartet",
    connected: "Verbunden",
    ended: "Gespräch beendet",
    failed: "Anruf fehlgeschlagen",
  };
  return labels[status] ?? "Anrufstatus wird ermittelt";
}

function formatDuration(seconds: number) {
  const safe = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const rest = safe % 60;
  return [hours, minutes, rest]
    .map((value) => String(value).padStart(2, "0"))
    .slice(hours ? 0 : 1)
    .join(":");
}

export function CrmPhoneCallPanel({
  initialCall,
  onClose,
  onCommunicationCreated,
  canEdit = true,
}: {
  initialCall: JsonRecord;
  onClose: () => void;
  onCommunicationCreated: (call?: JsonRecord) => void;
  canEdit?: boolean;
}) {
  const id = entityId(initialCall);
  const queryClient = useQueryClient();
  const { notify } = useNotifications();
  const latestCallRef = useRef<JsonRecord>({});
  const [discardOpen, setDiscardOpen] = useState(false);
  const [now, setNow] = useState(0);
  const [notes, setNotes] = useState(readString(initialCall, "notes"));
  const [noteVersion, setNoteVersion] = useState<number | null>(null);
  const [expectedNotes, setExpectedNotes] = useState<string | null>(null);
  const [savedNotesComparison, setSavedNotesComparison] = useState<string | null>(null);
  const [notesDirty, setNotesDirty] = useState(false);
  const initialDirection = readString(initialCall, "direction") || "outbound";
  const callQuery = useQuery({
    queryKey: ["crm", "phone-call", id],
    queryFn: ({ signal }) =>
      initialDirection === "outbound" && canEdit
        ? api.post<JsonRecord>("/crm/phone-calls/" + encodeURIComponent(id) + "/reconcile")
        : api.get<JsonRecord>("/crm/phone-calls/" + encodeURIComponent(id), signal),
    enabled: Boolean(id),
    initialData: { item: initialCall },
    refetchInterval: (query) => {
      const call = asRecord(asRecord(query.state.data).item);
      return ["ended", "failed"].includes(readString(call, "status")) ? 30_000 : 2_000;
    },
  });
  const call = asRecord(asRecord(callQuery.data).item);
  const status = readString(call, "status") || "initiating";
  const direction = readString(call, "direction") || "outbound";
  const incoming = direction === "inbound";
  const providerEventId = readString(call, "providerEventId", "provider_event_id");
  const finished = status === "ended" || status === "failed";
  const communicationId = readString(call, "communicationId", "communication_id");
  const callVersion = readNumber(call, "version");
  const persistedNotes = readString(call, "notes");
  const connectedAt = readString(call, "connectedAt", "connected_at");
  const connectedAtTimestamp = connectedAt ? new Date(connectedAt).getTime() : Number.NaN;
  const duration = finished
    ? readNumber(call, "durationSeconds", "duration_seconds")
    : Number.isFinite(connectedAtTimestamp) && now > 0
      ? Math.max(0, Math.floor((now - connectedAtTimestamp) / 1_000))
      : 0;

  useEffect(() => {
    latestCallRef.current = call;
  }, [call]);

  useEffect(() => {
    if (finished) onCommunicationCreated(latestCallRef.current);
  }, [callVersion, communicationId, finished, onCommunicationCreated]);

  useEffect(() => {
    if (finished || !connectedAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [connectedAt, finished]);

  useEffect(() => {
    if (!notesDirty) return;
    const guard = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [notesDirty]);

  const saveNotes = useMutation({
    mutationFn: () =>
      api.patch<JsonRecord>("/crm/phone-calls/" + encodeURIComponent(id) + "/notes", {
        notes,
        version: noteVersion ?? readNumber(call, "version"),
        expectedNotes: expectedNotes ?? persistedNotes,
      }),
    onSuccess: (payload) => {
      queryClient.setQueryData(["crm", "phone-call", id], payload);
      setNotesDirty(false);
      setNoteVersion(null);
      setExpectedNotes(null);
      setSavedNotesComparison(null);
      if (finished) onCommunicationCreated(asRecord(asRecord(payload).item));
      notify(
        readString(call, "organizationId", "organization_id")
          ? "Telefonnotiz wurde in der Kundenakte gespeichert."
          : "Telefonnotiz wurde unter Mehr → Nicht zugeordnete Anrufe gespeichert.",
      );
    },
    onError: (error) =>
      notify(
        error instanceof Error
          ? error.message
          : "Die Telefonnotiz konnte nicht gespeichert werden.",
        "error",
      ),
  });
  const loadCurrentNotes = useMutation({
    mutationFn: () => api.get<JsonRecord>("/crm/phone-calls/" + encodeURIComponent(id)),
    onSuccess: (payload) => {
      const latest = asRecord(payload.item);
      queryClient.setQueryData(["crm", "phone-call", id], payload);
      setNoteVersion(readNumber(latest, "version"));
      setExpectedNotes(readString(latest, "notes"));
      setSavedNotesComparison(readString(latest, "notes"));
      saveNotes.reset();
    },
  });
  const endCall = useMutation({
    mutationFn: () => api.post<JsonRecord>("/crm/phone-calls/" + encodeURIComponent(id) + "/end"),
    onSuccess: (payload) => {
      queryClient.setQueryData(["crm", "phone-call", id], payload);
      onCommunicationCreated(asRecord(asRecord(payload).item));
      notify(
        readString(call, "organizationId", "organization_id")
          ? "Das Gespräch wurde beendet und in der Kundenakte dokumentiert."
          : "Das Gespräch ist unter Mehr → Nicht zugeordnete Anrufe auffindbar.",
      );
    },
    onError: (error) =>
      notify(
        error instanceof Error ? error.message : "Der Anruf konnte nicht beendet werden.",
        "error",
      ),
  });
  const supportsHangup = readBoolean(call, "supportsHangup", "supports_hangup");
  const canHangup =
    supportsHangup && Boolean(readString(call, "providerCallId", "provider_call_id")) && !finished;

  const panel = (
    <aside
      className="crm-phone-call"
      aria-label={incoming ? "Eingehender Placetel-Anruf" : "Laufender Placetel-Anruf"}
    >
      <header>
        <span className="crm-phone-call__icon">
          <PhoneCall aria-hidden="true" />
        </span>
        <div>
          <small>{incoming ? "Eingehender Placetel-Anruf" : "Placetel-Anruf"}</small>
          <strong>{readString(call, "targetName", "target_name") || "Kontakt"}</strong>
          <span>{readString(call, "targetNumber", "target_number")}</span>
          {readString(call, "organizationId", "organization_id") ? (
            <Link
              className="crm-phone-call__record-link"
              to={crmOrganizationPath(readString(call, "organizationId", "organization_id"))}
            >
              Kundenakte öffnen
            </Link>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => (notesDirty ? setDiscardOpen(true) : onClose())}
          aria-label="Anruffenster schließen"
        >
          <X aria-hidden="true" />
        </button>
      </header>
      <div className="crm-phone-call__status">
        <span className={"crm-phone-call__pulse crm-phone-call__pulse--" + status} />
        <strong role="status">{statusLabel(status, direction, providerEventId)}</strong>
        {(status === "connected" || finished) && (
          <span role="timer" aria-live="off" aria-label="Gesprächsdauer">
            <Clock3 size={15} aria-hidden="true" /> {formatDuration(duration)}
          </span>
        )}
      </div>
      {canEdit ? (
        <Field label="Telefonnotiz" htmlFor={"phone-call-notes-" + id}>
          <Textarea
            id={"phone-call-notes-" + id}
            rows={4}
            disabled={saveNotes.isPending}
            value={notesDirty ? notes : persistedNotes}
            onChange={(event) => {
              if (!notesDirty) {
                setNoteVersion(readNumber(call, "version"));
                setExpectedNotes(persistedNotes);
              }
              setNotes(event.currentTarget.value);
              setNotesDirty(true);
            }}
            placeholder="Stichpunkte und Vereinbarungen festhalten …"
          />
        </Field>
      ) : null}
      {saveNotes.isError ? (
        <div className="crm-phone-call__hint" role="alert">
          <p>{saveNotes.error.message}</p>
          {saveNotes.error instanceof ApiError && saveNotes.error.status === 409 ? (
            <Button
              type="button"
              size="small"
              variant="secondary"
              busy={loadCurrentNotes.isPending}
              onClick={() => loadCurrentNotes.mutate()}
            >
              Aktuellen Stand laden
            </Button>
          ) : null}
        </div>
      ) : null}
      {loadCurrentNotes.isError ? <p role="alert">{loadCurrentNotes.error.message}</p> : null}
      {savedNotesComparison !== null ? (
        <div className="crm-phone-call__hint" role="status">
          <p>
            Ihr Entwurf bleibt erhalten. Vergleichen Sie ihn vor dem Speichern mit der gespeicherten
            Notiz:
          </p>
          <p style={{ whiteSpace: "pre-wrap" }}>
            {savedNotesComparison || "Keine gespeicherte Notiz"}
          </p>
        </div>
      ) : null}
      {canEdit ? (
        <div className="crm-phone-call__actions">
          <Button
            type="button"
            size="small"
            variant="secondary"
            busy={saveNotes.isPending}
            disabled={!notesDirty}
            onClick={() => saveNotes.mutate()}
          >
            <Save size={15} aria-hidden="true" /> Notiz speichern
          </Button>
          {canHangup ? (
            <Button
              type="button"
              size="small"
              variant="danger"
              busy={endCall.isPending}
              onClick={() => endCall.mutate()}
            >
              <PhoneOff size={15} aria-hidden="true" /> Auflegen
            </Button>
          ) : null}
        </div>
      ) : null}
      {!finished && !supportsHangup ? (
        <small className="crm-phone-call__hint">
          Das Gespräch wird am verwendeten Telefon beendet. Placetel meldet den Abschluss
          automatisch an die Kundenakte.
        </small>
      ) : null}
      {!incoming && status === "dialing" && providerEventId ? (
        <small className="crm-phone-call__hint">
          Placetel bestätigt die Annahme eines ausgehenden Gesprächs erst mit dem abschließenden
          Ereignis und der Gesprächsdauer.
        </small>
      ) : null}
      {finished && notesDirty ? (
        <small className="crm-phone-call__hint">
          Speichern Sie die geänderte Telefonnotiz, bevor Sie das Fenster schließen.
        </small>
      ) : null}
      {supportsHangup && !finished && !canHangup ? (
        <small className="crm-phone-call__hint">
          Auflegen wird verfügbar, sobald Placetel die Anrufsteuerung gemeldet hat.
        </small>
      ) : null}
      {incoming && !readString(call, "organizationId", "organization_id") ? (
        <small className="crm-phone-call__hint">
          Die Rufnummer konnte keiner eindeutigen Kundenakte zugeordnet werden. Notizen bleiben
          unter <Link to="/crm/anrufe">Nicht zugeordnete Anrufe</Link> auffindbar.
        </small>
      ) : null}
    </aside>
  );
  if (callQuery.error instanceof ApiError && [403, 404].includes(callQuery.error.status))
    return null;
  const stack = document.getElementById("crm-call-stack");
  return (
    <>
      {stack ? createPortal(panel, stack) : panel}
      <ConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Telefonnotiz verwerfen?"
        description="Ihre ungespeicherte Telefonnotiz geht verloren. Der Anruf bleibt gespeichert."
        destructive
        cancelAction={
          <Button variant="secondary" onClick={() => setDiscardOpen(false)}>
            Weiter bearbeiten
          </Button>
        }
        confirmAction={
          <Button variant="danger" onClick={onClose}>
            Notiz verwerfen und schließen
          </Button>
        }
      >
        {null}
      </ConfirmDialog>
    </>
  );
}
